import React from "react";
import { Navigate, useParams } from "react-router-dom";

import TitleButtons from "../../../../components/TitleButtons";
import TableActions from "../../../../components/TableActions";
import HistoricoWidget, {
  HistoricoItem,
} from "../../../../components/tableHistorico";
import HistoricoPrecosWidget from "../../../../components/tablePrice";
import { useAuth } from "../../../../context/auth-context";

type CurrencyCode = "BRL" | "USD" | "EUR";

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

type ProdutoAnyAPI = {
  id?: number | string | null;
  produto_id?: number | string | null;

  codigo?: string | null;
  ncm?: string | null;
  nome?: string | null;
  classificacao?: string | null;
  posicao?: string | null;
  vendavel?: boolean | null;
  responsavel_registro?: number | string | null;
  departamento_legado?: string | null;
  departamento?: string | null;
  peso?: string | number | null;
  dimensao?: string | null;
  criado_em?: string | null;
  atualizado_em?: string | null;
  ocultar?: boolean | null;
  empresa_id?: number | string | null;

  estoque_id?: number | string | null;
  estoque_codigo?: string | null;
  estoque_nome?: string | null;

  modulo_id?: number | string | null;
  modulo_codigo?: string | null;
  modulo_label?: string | null;

  quantidade_atual?: number | string | null;
  custo_unitario?: string | number | null;
  custo_unitario_atual?: string | number | null;
  ultimo_custo_unitario?: string | number | null;
  moeda?: string | null;
  ultima_moeda?: string | null;
};

type ApiMovItem = HistoricoItem & {
  id?: number | string | null;
  item_id?: number | string | null;
  movimentacao_id?: number | string | null;
  produto_id?: number | string | null;

  tipo?: string | null;
  tipo_full?: string | null;
  codigo_movimentacao?: string | null;
  tipo_codigo?: string | null;
  tipo_descricao?: string | null;

  quantidade?: number | string | null;
  custo_unitario?: number | string | null;
  moeda?: string | null;

  data_movimentacao?: string | null;
  criado_em?: string | null;

  responsavel_id?: number | string | null;
  responsavel_nome?: string | null;

  motivo?: string | null;
  os_numero?: string | null;
  proposta_venda?: string | null;
  origem_mov_id?: number | string | null;
};

type MovimentosPorTipo = {
  entrada?: ApiMovItem[];
  saida?: ApiMovItem[];
  perda?: ApiMovItem[];
  venda?: ApiMovItem[];
  consumo?: ApiMovItem[];
  retorno?: ApiMovItem[];
  standby?: ApiMovItem[];
  ajuste?: ApiMovItem[];
  [key: string]: ApiMovItem[] | undefined;
};

type ProdutoDetalhePayload = {
  produto: ProdutoAnyAPI;
  custos: ApiMovItem[];
  movimentos_por_tipo?: MovimentosPorTipo;
  estoque_atual: {
    produto_id?: number | string | null;
    estoque_id?: number | string | null;
    quantidade_atual?: number | string | null;
    custo_unitario_atual?: string | number | null;
    moeda?: string | null;
  } | null;
};

type GetProdutoResp = {
  ok: boolean;
  erro?: string;
  erros?: string[];

  fonte?: "cache" | "banco";
  versao?: number;

  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    empresa_id?: number | string;
    modulo_id?: number | string;
  };

  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
    icone?: string | null;
    path?: string | null;
  };

  data?: ProdutoAnyAPI[] | ProdutoDetalhePayload | null;
  visiveis?: ProdutoAnyAPI[] | null;
  ocultos?: ProdutoAnyAPI[] | null;

  oculto?: boolean;
};

type PostEditarProdutoResp = {
  ok: boolean;
  erro?: string;
  erros?: string[];
  trace_id?: string;
  data?: {
    produto?: ProdutoAnyAPI;
  };
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    empresa_id?: number | string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
    icone?: string | null;
    path?: string | null;
  };
};

type PostReajustarPrecoResp = {
  ok: boolean;
  erro?: string;
  erros?: string[];
  mensagem?: string;
  trace_id?: string;
  data?: {
    produto?: {
      id?: number | string;
      codigo?: string;
      nome?: string;
    };
    reajuste?: {
      auditoria_id?: number | string | null;
      movimentacao_id?: number | string | null;
      movimentacao_item_id?: number | string | null;
      data_lancamento?: string | null;
      custo_anterior?: string | number | null;
      custo_novo?: string | number | null;
      moeda_anterior?: string | null;
      moeda_nova?: string | null;
      motivo?: string | null;
    };
    estoque_atual?: unknown;
    item_atualizado?: unknown;
  };
};

type EstoqueConfig = {
  areaSlug: string;
  estoqueSlug: string;
  title: string;
  detalheBasePath: string;
};

type EditFormState = {
  codigo: string;
  nome: string;
  ncm: string;
  classificacao: string;
  posicao: string;
  vendavel: boolean;
  ocultar: boolean;
  peso: string;
  dimensao: string;
};

type ReajustePrecoFormState = {
  novo_custo_unitario: string;
  moeda: CurrencyCode;
  motivo: string;
};

const API_LOGVI_BASE = "https://api.logvi-havox.com";

const URL_GET_PRODUTO = `${API_LOGVI_BASE}/template/estoque-administrativo/get-produto`;
const URL_POST_PRODUTO_EDITAR = `${API_LOGVI_BASE}/template/estoque-administrativo/produtos-editar`;
const URL_POST_REAJUSTAR_ULTIMO_PRECO = `${API_LOGVI_BASE}/template/estoque-administrativo/produtos-reajustar-ultimo-preco`;

const ACCESS_TOKEN_KEY = "access_token";

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part.toLowerCase() === "p") return "p/";
      if (part.toLowerCase() === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): EstoqueConfig {
  const area = normalizeSlug(areaSlug) || "suprimentos";
  const estoque = normalizeSlug(estoqueSlug) || "estoque";

  return {
    areaSlug: area,
    estoqueSlug: estoque,
    title: `${slugToTitle(area)} - Detalhes do Produto`,
    detalheBasePath: `/${area}/${estoque}/produtos`,
  };
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function readJsonSafe<T>(res: Response): Promise<T | null> {
  const text = await res.text();

  if (!text) return null;

  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  return Number.isFinite(n) ? n : null;
}

function normalizeCurrency(value: unknown): CurrencyCode | null {
  const s = String(value ?? "")
    .trim()
    .toUpperCase();

  if (s === "BRL" || s === "USD" || s === "EUR") return s;

  return null;
}

function formatCurrency(
  value: string | number | null | undefined,
  currency?: CurrencyCode | null
): string {
  if (value === null || value === undefined || value === "") return "—";

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  if (!Number.isFinite(n)) return "—";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency || "BRL",
  }).format(n);
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return "—";

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) return iso;

    const data = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${data} - ${hora}`;
  } catch {
    return iso || "—";
  }
}

function toApiMoney(raw: string): string {
  const s = String(raw ?? "").trim();

  if (!s) return "";

  const clean = s.replace(/[^0-9.,-]/g, "");
  const lastComma = clean.lastIndexOf(",");
  const lastDot = clean.lastIndexOf(".");
  const decPos = Math.max(lastComma, lastDot);

  if (decPos === -1) {
    const intPart = clean.replace(/\D/g, "") || "0";
    return `${intPart}.00`;
  }

  const intPart = clean.slice(0, decPos).replace(/\D/g, "") || "0";
  let fracPart = clean.slice(decPos + 1).replace(/\D/g, "");

  if (fracPart.length > 4) fracPart = fracPart.slice(0, 4);
  if (fracPart.length === 0) fracPart = "00";

  return `${intPart}.${fracPart}`;
}

function isValidPositiveMoney(raw: string): boolean {
  const api = toApiMoney(raw);
  const n = Number(api);

  return Number.isFinite(n) && n > 0;
}

function findModuloAtualFromSessao(
  sessao: any,
  config: EstoqueConfig
): ModuloAtual | null {
  const areaSlug = normalizeComparable(config.areaSlug);
  const estoqueSlug = normalizeComparable(config.estoqueSlug);

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoqueSlug] || estoqueSlug;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (areaSlug && depSlug && depSlug !== areaSlug) {
        continue;
      }

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoqueSlug ||
            modLabel === estoqueSlug ||
            modCodigo.includes(estoqueSlug) ||
            estoqueSlug.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

function isDetalhePayload(value: unknown): value is ProdutoDetalhePayload {
  return (
    !!value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    "produto" in value
  );
}

function normalizeProdutoFromResponse(
  json: GetProdutoResp,
  codigo: string
): ProdutoDetalhePayload | null {
  const code = String(codigo || "")
    .trim()
    .toUpperCase();

  if (isDetalhePayload(json.data)) {
    return json.data;
  }

  const produtos = Array.isArray(json.data)
    ? json.data
    : Array.isArray(json.visiveis)
    ? json.visiveis
    : [];

  const produto =
    produtos.find(
      (p) =>
        String(p.codigo || "")
          .trim()
          .toUpperCase() === code
    ) ?? produtos[0];

  if (!produto) return null;

  return {
    produto,
    custos: [],
    movimentos_por_tipo: {},
    estoque_atual: {
      produto_id: produto.id ?? produto.produto_id ?? null,
      estoque_id: produto.estoque_id ?? json.estoque?.id ?? null,
      quantidade_atual: produto.quantidade_atual ?? null,
      custo_unitario_atual:
        produto.custo_unitario_atual ??
        produto.custo_unitario ??
        produto.ultimo_custo_unitario ??
        null,
      moeda: produto.moeda ?? produto.ultima_moeda ?? null,
    },
  };
}

function getMovimentoDate(item: ApiMovItem | null | undefined): string | null {
  if (!item) return null;

  return item.data_movimentacao || item.criado_em || null;
}

function getMovimentoTimestamp(item: ApiMovItem | null | undefined): number {
  const data = getMovimentoDate(item);

  if (!data) return 0;

  const t = Date.parse(data);

  return Number.isFinite(t) ? t : 0;
}

function normalizeMovimentoItem(item: ApiMovItem): ApiMovItem {
  const data =
    item.data_movimentacao ?? item.criado_em ?? (item as any).data ?? null;

  const tipoFull = String(item.tipo_full || item.tipo || "").toLowerCase();

  let tipo = item.tipo || item.tipo_full || "Movimentação";

  if (tipoFull.includes("retorno")) tipo = "Retorno";
  else if (
    tipoFull === "ajuste" ||
    (tipoFull.includes("ajuste") && !tipoFull.includes("preco"))
  ) {
    tipo = "Ajuste";
  } else if (tipoFull.includes("entrada")) tipo = "Entrada";
  else if (tipoFull.includes("servico") || tipoFull.includes("serviço"))
    tipo = "Saída Serviço";
  else if (tipoFull.includes("venda")) tipo = "Venda";
  else if (tipoFull.includes("perda")) tipo = "Perda";
  else if (tipoFull.includes("consumo")) tipo = "Consumo";
  else if (tipoFull.includes("preco") || tipoFull.includes("preço"))
    tipo = "Ajuste de Preço";

  return {
    ...item,
    tipo,
    data_movimentacao: data,
  };
}

function isPriceMovement(item: ApiMovItem): boolean {
  const t = String(item.tipo_full || item.tipo || "").toLowerCase();

  return t.includes("preco") || t.includes("preço");
}

function isMovimentoDerivadoFront(item: ApiMovItem): boolean {
  const tipo = String(item.tipo || "")
    .trim()
    .toLowerCase();

  const tipoFull = String(item.tipo_full || "")
    .trim()
    .toLowerCase();

  const codigoMov = String(item.codigo_movimentacao || "")
    .trim()
    .toUpperCase();

  const tipoCodigo = String((item as any).tipo_codigo || "")
    .trim()
    .toUpperCase();

  const origemMovId = (item as any).origem_mov_id;

  const base = `${tipo} ${tipoFull}`.toLowerCase();

  const temOrigem =
    origemMovId !== null &&
    origemMovId !== undefined &&
    String(origemMovId).trim() !== "" &&
    String(origemMovId).trim() !== "0";

  const isRetorno =
    base.includes("retorno") ||
    codigoMov.includes("RETORNO") ||
    tipoCodigo.includes("RETORNO") ||
    codigoMov === "RET-ESTOQUE" ||
    tipoCodigo === "RET-ESTOQUE";

  if (isRetorno) return false;

  const isPerda =
    codigoMov === "BAIXA-PERDA" ||
    codigoMov === "SAI-PERDA" ||
    tipoCodigo === "BAIXA-PERDA" ||
    tipoCodigo === "SAI-PERDA" ||
    tipo === "perda" ||
    tipoFull === "perda" ||
    base.includes("perda");

  if (isPerda) return false;

  const isCancelamento =
    base.includes("cancelamento") ||
    base.includes("cancelado") ||
    codigoMov.includes("CANCEL") ||
    tipoCodigo.includes("CANCEL");

  if (isCancelamento) return true;

  return temOrigem;
}

function extractMovimentos(
  payload: ProdutoDetalhePayload | null
): ApiMovItem[] {
  const mov = payload?.movimentos_por_tipo;

  if (!mov) return [];

  const all = Object.values(mov).flatMap((arr) =>
    Array.isArray(arr) ? arr : []
  ) as ApiMovItem[];

  return all
    .filter((item) => !isMovimentoDerivadoFront(item))
    .filter((item) => !isPriceMovement(item))
    .map(normalizeMovimentoItem)
    .sort((a, b) => getMovimentoTimestamp(b) - getMovimentoTimestamp(a));
}

function extractPrecos(payload: ProdutoDetalhePayload | null): ApiMovItem[] {
  const custos = Array.isArray(payload?.custos) ? payload?.custos ?? [] : [];

  const mov = payload?.movimentos_por_tipo;

  const allMov = mov
    ? (Object.values(mov).flatMap((arr) =>
        Array.isArray(arr) ? arr : []
      ) as ApiMovItem[])
    : [];

  const ajustesPreco = allMov.filter(isPriceMovement);

  return [...custos, ...ajustesPreco]
    .map((item) => ({
      ...normalizeMovimentoItem(item),
      tipo: isPriceMovement(item) ? "Ajuste de Preço" : item.tipo || "Preço",
    }))
    .sort((a, b) => getMovimentoTimestamp(b) - getMovimentoTimestamp(a));
}

function isWithinLastDays(dateIso: string | null | undefined, days: number) {
  if (!dateIso) return false;

  const t = Date.parse(dateIso);

  if (!Number.isFinite(t)) return false;

  const now = Date.now();
  const diff = now - t;
  const limit = days * 24 * 60 * 60 * 1000;

  return diff >= 0 && diff <= limit;
}

function normalizeText(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function isEntradaMovimento(item: ApiMovItem | null | undefined): boolean {
  if (!item) return false;

  const tipo = normalizeText(item.tipo);
  const tipoFull = normalizeText(item.tipo_full);

  const codigo = String(item.codigo_movimentacao || "")
    .trim()
    .toUpperCase();

  const tipoCodigo = String((item as any).tipo_codigo || "")
    .trim()
    .toUpperCase();

  return (
    codigo.startsWith("ENT") ||
    tipoCodigo.startsWith("ENT") ||
    tipo.includes("entrada") ||
    tipoFull.includes("entrada")
  );
}

function hasValidCusto(item: ApiMovItem | null | undefined): boolean {
  if (!item) return false;

  const n =
    typeof item.custo_unitario === "number"
      ? item.custo_unitario
      : Number(String(item.custo_unitario ?? "").replace(",", "."));

  return Number.isFinite(n) && n > 0;
}

function formatQuantidade(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";

  const n =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));

  if (!Number.isFinite(n)) return String(value);

  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 4,
  }).format(n);
}

const NoticeBanner: React.FC<{
  type: "success" | "error" | "notify";
  children: React.ReactNode;
}> = ({ type, children }) => {
  let bg = "#e7f1ff";
  let color = "#084298";
  let border = "#b6d4fe";

  if (type === "success") {
    bg = "#e6ffed";
    color = "#0f5132";
    border = "#badbcc";
  }

  if (type === "error") {
    bg = "#ffecec";
    color = "#842029";
    border = "#f5c2c7";
  }

  return (
    <div
      role="alert"
      style={{
        margin: "12px 40px 0 40px",
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        border,
        borderStyle: "solid",
        borderWidth: 1,
        fontFamily: "Inter, sans-serif",
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div style={{ marginBottom: "24px" }}>
    <div
      style={{
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: "15px",
        marginBottom: "4px",
      }}
    >
      {label}
    </div>

    <div style={{ fontFamily: "Inter", fontWeight: 300, fontSize: "14px" }}>
      {value}
    </div>
  </div>
);

const EditField: React.FC<{
  label: string;
  value: string;
  disabled?: boolean;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  onChange: (value: string) => void;
}> = ({ label, value, disabled, inputMode, onChange }) => (
  <label
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 6,
      fontFamily: "Inter, sans-serif",
    }}
  >
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: "#334155",
      }}
    >
      {label}
    </span>

    <input
      type="text"
      inputMode={inputMode}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 38,
        border: "1px solid #cbd5e1",
        borderRadius: 8,
        padding: "0 11px",
        fontFamily: "Inter, sans-serif",
        fontSize: 13,
        color: "#0f172a",
        background: disabled ? "#f8fafc" : "#fff",
        outline: "none",
      }}
    />
  </label>
);

const SelectField: React.FC<{
  label: string;
  value: CurrencyCode;
  disabled?: boolean;
  onChange: (value: CurrencyCode) => void;
}> = ({ label, value, disabled, onChange }) => (
  <label
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 6,
      fontFamily: "Inter, sans-serif",
    }}
  >
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: "#334155",
      }}
    >
      {label}
    </span>

    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as CurrencyCode)}
      style={{
        height: 38,
        border: "1px solid #cbd5e1",
        borderRadius: 8,
        padding: "0 11px",
        fontFamily: "Inter, sans-serif",
        fontSize: 13,
        color: "#0f172a",
        background: disabled ? "#f8fafc" : "#fff",
        outline: "none",
      }}
    >
      <option value="BRL">BRL</option>
      <option value="USD">USD</option>
      <option value="EUR">EUR</option>
    </select>
  </label>
);

const TextAreaField: React.FC<{
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}> = ({ label, value, disabled, onChange }) => (
  <label
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 6,
      fontFamily: "Inter, sans-serif",
    }}
  >
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: "#334155",
      }}
    >
      {label}
    </span>

    <textarea
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      maxLength={500}
      style={{
        resize: "vertical",
        minHeight: 84,
        border: "1px solid #cbd5e1",
        borderRadius: 8,
        padding: "9px 11px",
        fontFamily: "Inter, sans-serif",
        fontSize: 13,
        color: "#0f172a",
        background: disabled ? "#f8fafc" : "#fff",
        outline: "none",
      }}
    />
  </label>
);

const ModalShell: React.FC<{
  title: string;
  subtitle?: string;
  ariaLabel: string;
  disabled?: boolean;
  onClose: () => void;
  width?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, ariaLabel, disabled, onClose, width, children }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label={ariaLabel}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(15, 23, 42, 0.42)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
    }}
  >
    <div
      style={{
        width: width || "min(860px, 100%)",
        maxHeight: "calc(100vh - 48px)",
        overflow: "auto",
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 18px 60px rgba(15, 23, 42, 0.25)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          padding: "18px 22px",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 17,
              fontWeight: 700,
              color: "#0f172a",
            }}
          >
            {title}
          </div>

          {subtitle && (
            <div
              style={{
                marginTop: 4,
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                color: "#64748b",
              }}
            >
              {subtitle}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onClose}
          disabled={disabled}
          style={{
            border: "1px solid #cbd5e1",
            background: "#fff",
            color: "#0f172a",
            borderRadius: 8,
            height: 34,
            padding: "0 12px",
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            fontWeight: 600,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          Fechar
        </button>
      </div>

      <div style={{ padding: 22 }}>{children}</div>
    </div>
  </div>
);

const DetalhesProdutoGenerico: React.FC = () => {
  const {
    areaSlug,
    estoqueSlug,
    codigo: codigoParam,
  } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
    codigo: string;
  }>();

  const codigo = React.useMemo(
    () => decodeURIComponent(codigoParam ?? ""),
    [codigoParam]
  );

  const { sessao, loading: authLoading } = useAuth();

  const config = React.useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const moduloAtual = React.useMemo(
    () => findModuloAtualFromSessao(sessao, config),
    [sessao, config]
  );

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<ProdutoDetalhePayload | null>(
    null
  );
  const [isOculto, setIsOculto] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<"mov" | "precos">("mov");

  const [showEditOverlay, setShowEditOverlay] = React.useState(false);
  const [editLoading, setEditLoading] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);
  const [editSuccess, setEditSuccess] = React.useState<string | null>(null);

  const [editForm, setEditForm] = React.useState<EditFormState>({
    codigo: "",
    nome: "",
    ncm: "",
    classificacao: "",
    posicao: "",
    vendavel: false,
    ocultar: false,
    peso: "",
    dimensao: "",
  });

  const [showReajusteOverlay, setShowReajusteOverlay] = React.useState(false);
  const [reajusteLoading, setReajusteLoading] = React.useState(false);
  const [reajusteError, setReajusteError] = React.useState<string | null>(null);
  const [reajusteSuccess, setReajusteSuccess] = React.useState<string | null>(
    null
  );

  const [reajusteForm, setReajusteForm] =
    React.useState<ReajustePrecoFormState>({
      novo_custo_unitario: "",
      moeda: "BRL",
      motivo: "",
    });

  const fetchProduto = React.useCallback(
    async (signal?: AbortSignal) => {
      if (!codigo || !moduloAtual?.id) return;

      setLoading(true);
      setError(null);

      try {
        const res = await fetch(URL_GET_PRODUTO, {
          method: "POST",
          headers: buildAuthHeaders(true),
          cache: "no-store",
          signal,
          body: JSON.stringify({
            modulo_id: moduloAtual.id,
            codigo,
            modo: "detalhe",
            include_historico: true,
          }),
        });

        const json = (await readJsonSafe<GetProdutoResp>(res)) ?? {
          ok: res.ok,
          data: null,
        };

        if (!res.ok || json.ok === false) {
          const msg =
            json.erro ||
            (Array.isArray(json.erros) && json.erros.join(" | ")) ||
            `HTTP ${res.status}`;

          throw new Error(msg);
        }

        const normalized = normalizeProdutoFromResponse(json, codigo);

        if (!normalized) {
          throw new Error("Produto não encontrado.");
        }

        setPayload(normalized);
        setIsOculto(
          Boolean(json.oculto) || Boolean(normalized.produto?.ocultar)
        );
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          setError(e?.message || "Falha ao buscar produto.");
          setPayload(null);
          setIsOculto(false);
        }
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [codigo, moduloAtual?.id]
  );

  React.useEffect(() => {
    if (authLoading) return;

    if (!codigo) return;

    if (!moduloAtual?.id) {
      setLoading(false);
      setError("Módulo de estoque não identificado para esta rota.");
      return;
    }

    const abort = new AbortController();

    fetchProduto(abort.signal);

    return () => abort.abort();
  }, [authLoading, codigo, moduloAtual?.id, fetchProduto]);

  const produto = payload?.produto ?? null;

  const produtoId = React.useMemo(() => {
    return toNumberOrNull(produto?.id ?? produto?.produto_id);
  }, [produto?.id, produto?.produto_id]);

  const moedaAtual = React.useMemo<CurrencyCode>(() => {
    return (
      normalizeCurrency(payload?.estoque_atual?.moeda) ||
      normalizeCurrency(produto?.moeda) ||
      normalizeCurrency(produto?.ultima_moeda) ||
      "BRL"
    );
  }, [payload?.estoque_atual?.moeda, produto?.moeda, produto?.ultima_moeda]);

  const movimentosOrdenados = React.useMemo(
    () => extractMovimentos(payload),
    [payload]
  );

  const precoHistoricoItems = React.useMemo(
    () => extractPrecos(payload),
    [payload]
  );

  const ultimaMovimentacao = movimentosOrdenados[0] ?? null;

  const ultimaMovimentacaoEhEntrada = React.useMemo(
    () => isEntradaMovimento(ultimaMovimentacao),
    [ultimaMovimentacao]
  );

  const ultimaMovimentacaoDentroPrazo = React.useMemo(
    () => isWithinLastDays(getMovimentoDate(ultimaMovimentacao), 3),
    [ultimaMovimentacao]
  );

  const podeReajustarUltimoPreco = React.useMemo(() => {
    return (
      !!produto &&
      !!produtoId &&
      !!moduloAtual?.id &&
      !!ultimaMovimentacao &&
      ultimaMovimentacaoEhEntrada &&
      ultimaMovimentacaoDentroPrazo &&
      hasValidCusto(ultimaMovimentacao)
    );
  }, [
    produto,
    produtoId,
    moduloAtual?.id,
    ultimaMovimentacao,
    ultimaMovimentacaoEhEntrada,
    ultimaMovimentacaoDentroPrazo,
  ]);

  const motivoBloqueioReajuste = React.useMemo(() => {
    if (loading) return "Carregando movimentações.";
    if (!ultimaMovimentacao) return "Não há movimentações para este produto.";
    if (!ultimaMovimentacaoEhEntrada)
      return "A última movimentação não é uma entrada.";
    if (!ultimaMovimentacaoDentroPrazo)
      return "A última entrada está fora do prazo de 3 dias.";
    if (!hasValidCusto(ultimaMovimentacao))
      return "A última entrada não possui custo unitário válido.";
    if (!produtoId) return "Produto sem ID válido.";
    if (!moduloAtual?.id) return "Módulo não identificado.";

    return null;
  }, [
    loading,
    ultimaMovimentacao,
    ultimaMovimentacaoEhEntrada,
    ultimaMovimentacaoDentroPrazo,
    produtoId,
    moduloAtual?.id,
  ]);

  const nome = produto?.nome ?? "—";
  const ncm = produto?.ncm ?? "—";
  const classificacao = produto?.classificacao ?? "—";
  const posicao = produto?.posicao ?? "—";
  const dimensao = produto?.dimensao ?? "—";
  const peso =
    produto?.peso !== null &&
    produto?.peso !== undefined &&
    produto?.peso !== ""
      ? String(produto.peso)
      : "—";

  const quantidadeEstoque = React.useMemo(() => {
    const q =
      payload?.estoque_atual?.quantidade_atual ?? produto?.quantidade_atual;
    return q === null || q === undefined ? "—" : String(q);
  }, [payload?.estoque_atual?.quantidade_atual, produto?.quantidade_atual]);

  const custoUnitario = React.useMemo(() => {
    const atual =
      payload?.estoque_atual?.custo_unitario_atual ??
      produto?.custo_unitario_atual ??
      produto?.custo_unitario ??
      produto?.ultimo_custo_unitario ??
      null;

    return formatCurrency(atual, moedaAtual);
  }, [
    payload?.estoque_atual?.custo_unitario_atual,
    produto?.custo_unitario_atual,
    produto?.custo_unitario,
    produto?.ultimo_custo_unitario,
    moedaAtual,
  ]);

  const tituloPagina = React.useMemo(() => {
    const area = slugToTitle(config.areaSlug);
    const estoque =
      produto?.estoque_nome ||
      payload?.produto?.modulo_label ||
      moduloAtual?.label ||
      slugToTitle(config.estoqueSlug);

    return `${area} - Detalhes do Produto / ${codigo} - ${estoque}`;
  }, [
    config.areaSlug,
    config.estoqueSlug,
    produto?.estoque_nome,
    payload?.produto?.modulo_label,
    moduloAtual?.label,
    codigo,
  ]);

  function handleOpenEditOverlay() {
    if (!produto) return;

    setEditError(null);
    setEditSuccess(null);

    setEditForm({
      codigo: String(produto.codigo ?? codigo ?? ""),
      nome: String(produto.nome ?? ""),
      ncm: String(produto.ncm ?? ""),
      classificacao: String(produto.classificacao ?? ""),
      posicao: String(produto.posicao ?? ""),
      vendavel: Boolean(produto.vendavel),
      ocultar: Boolean(produto.ocultar),
      peso:
        produto.peso !== null &&
        produto.peso !== undefined &&
        produto.peso !== ""
          ? String(produto.peso)
          : "",
      dimensao: String(produto.dimensao ?? ""),
    });

    setShowEditOverlay(true);
  }

  function handleCloseEditOverlay() {
    if (editLoading) return;

    setShowEditOverlay(false);
    setEditError(null);
    setEditSuccess(null);
  }

  function handleOpenReajusteOverlay() {
    if (!podeReajustarUltimoPreco || !ultimaMovimentacao) {
      setReajusteError(
        motivoBloqueioReajuste ||
          "O último lançamento não está elegível para reajuste."
      );
      return;
    }

    const moeda =
      normalizeCurrency(ultimaMovimentacao.moeda) ||
      normalizeCurrency(payload?.estoque_atual?.moeda) ||
      moedaAtual ||
      "BRL";

    setReajusteError(null);
    setReajusteSuccess(null);

    setReajusteForm({
      novo_custo_unitario:
        ultimaMovimentacao.custo_unitario !== null &&
        ultimaMovimentacao.custo_unitario !== undefined
          ? String(ultimaMovimentacao.custo_unitario).replace(".", ",")
          : "",
      moeda,
      motivo: "",
    });

    setShowReajusteOverlay(true);
  }

  function handleCloseReajusteOverlay() {
    if (reajusteLoading) return;

    setShowReajusteOverlay(false);
    setReajusteError(null);
    setReajusteSuccess(null);
  }

  async function handleSubmitEditProduto() {
    if (!moduloAtual?.id) {
      setEditError("Módulo de estoque não identificado para esta rota.");
      return;
    }

    if (!produtoId) {
      setEditError("Produto sem ID numérico válido para edição.");
      return;
    }

    const codigoEditado = editForm.codigo.trim().toUpperCase();
    const nomeEditado = editForm.nome.trim();

    if (!codigoEditado) {
      setEditError("O código do produto é obrigatório.");
      return;
    }

    if (!nomeEditado) {
      setEditError("O nome do produto é obrigatório.");
      return;
    }

    setEditLoading(true);
    setEditError(null);
    setEditSuccess(null);

    try {
      const res = await fetch(URL_POST_PRODUTO_EDITAR, {
        method: "POST",
        headers: buildAuthHeaders(true),
        cache: "no-store",
        body: JSON.stringify({
          modulo_id: moduloAtual.id,
          id: produtoId,
          codigo: codigoEditado,
          nome: nomeEditado,
          ncm: editForm.ncm.trim() || null,
          classificacao: editForm.classificacao.trim() || null,
          posicao: editForm.posicao.trim() || null,
          vendavel: editForm.vendavel,
          ocultar: editForm.ocultar,
          peso: editForm.peso.trim() || null,
          dimensao: editForm.dimensao.trim() || null,
        }),
      });

      const json = await readJsonSafe<PostEditarProdutoResp>(res);

      if (!res.ok || json?.ok === false) {
        const msg =
          json?.erro ||
          (Array.isArray(json?.erros) ? json?.erros.join(" | ") : "") ||
          `HTTP ${res.status}`;

        throw new Error(msg);
      }

      setEditSuccess("Produto atualizado com sucesso.");

      await fetchProduto();

      window.setTimeout(() => {
        setShowEditOverlay(false);
        setEditSuccess(null);
      }, 700);
    } catch (e: any) {
      setEditError(e?.message || "Falha ao atualizar produto.");
    } finally {
      setEditLoading(false);
    }
  }

  async function handleSubmitReajustePreco() {
    if (!moduloAtual?.id) {
      setReajusteError("Módulo de estoque não identificado para esta rota.");
      return;
    }

    if (!produtoId) {
      setReajusteError("Produto sem ID numérico válido para reajuste.");
      return;
    }

    if (!podeReajustarUltimoPreco) {
      setReajusteError(
        motivoBloqueioReajuste ||
          "O último lançamento não está elegível para reajuste."
      );
      return;
    }

    const novoCustoApi = toApiMoney(reajusteForm.novo_custo_unitario);
    const motivo = reajusteForm.motivo.trim();

    if (!isValidPositiveMoney(reajusteForm.novo_custo_unitario)) {
      setReajusteError("Informe um novo custo unitário válido maior que zero.");
      return;
    }

    if (!motivo) {
      setReajusteError("Informe o motivo do reajuste.");
      return;
    }

    setReajusteLoading(true);
    setReajusteError(null);
    setReajusteSuccess(null);

    try {
      const res = await fetch(URL_POST_REAJUSTAR_ULTIMO_PRECO, {
        method: "POST",
        headers: buildAuthHeaders(true),
        cache: "no-store",
        body: JSON.stringify({
          modulo_id: moduloAtual.id,
          produto_id: produtoId,
          novo_custo_unitario: novoCustoApi,
          moeda: reajusteForm.moeda,
          motivo,
        }),
      });

      const json = await readJsonSafe<PostReajustarPrecoResp>(res);

      if (!res.ok || json?.ok === false) {
        const msg =
          json?.erro ||
          (Array.isArray(json?.erros) ? json.erros.join(" | ") : "") ||
          `HTTP ${res.status}`;

        throw new Error(msg);
      }

      setReajusteSuccess(
        json?.mensagem ||
          "Preço do último lançamento de entrada reajustado com sucesso."
      );

      await fetchProduto();

      window.setTimeout(() => {
        setShowReajusteOverlay(false);
        setReajusteSuccess(null);
        setActiveTab("precos");
      }, 900);
    } catch (e: any) {
      setReajusteError(e?.message || "Falha ao reajustar preço.");
    } finally {
      setReajusteLoading(false);
    }
  }

  const toggleWrapStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    padding: "0 40px",
    margin: "8px 0 12px 0",
    alignItems: "center",
  };

  const baseBtn: React.CSSProperties = {
    padding: "8px 14px",
    borderRadius: 4,
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #ccc",
    background: "#09363f",
    color: "#fff",
    outline: "none",
    userSelect: "none",
    transition:
      "transform 0.02s ease-in-out, box-shadow 0.1s ease-in-out, opacity 0.12s",
  };

  const btnActive: React.CSSProperties = {
    ...baseBtn,
    boxShadow: "0 0 0 1px rgba(18,66,128,0.15)",
    opacity: 1,
  };

  const btnInactive: React.CSSProperties = {
    ...baseBtn,
    opacity: 0.7,
  };

  const smallActionButton: React.CSSProperties = {
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #09363f",
    background: "#fff",
    color: "#09363f",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  };

  if (!codigo) {
    return <Navigate to={config.detalheBasePath} replace />;
  }

  return (
    <div>
      <TitleButtons
        fullBleedLine
        title={tituloPagina}
        buttonMarginRight="40px"
      />

      <TableActions
        buttons={[
          {
            label: "Alterar dados do Produto",
            onClick: handleOpenEditOverlay,
            variant: "primary",
          },
        ]}
      />

      {showEditOverlay && (
        <ModalShell
          title="Alterar dados do produto"
          subtitle={`${codigo} — edita somente dados cadastrais. Quantidade, custo e moeda não são alterados por esta tela.`}
          ariaLabel="Alterar dados do produto"
          disabled={editLoading}
          onClose={handleCloseEditOverlay}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 16,
            }}
          >
            <EditField
              label="Código"
              value={editForm.codigo}
              disabled={editLoading}
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, codigo: value }))
              }
            />

            <EditField
              label="Nome do Produto"
              value={editForm.nome}
              disabled={editLoading}
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, nome: value }))
              }
            />

            <EditField
              label="NCM"
              value={editForm.ncm}
              disabled={editLoading}
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, ncm: value }))
              }
            />

            <EditField
              label="Classificação"
              value={editForm.classificacao}
              disabled={editLoading}
              onChange={(value) =>
                setEditForm((prev) => ({
                  ...prev,
                  classificacao: value,
                }))
              }
            />

            <EditField
              label="Posição"
              value={editForm.posicao}
              disabled={editLoading}
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, posicao: value }))
              }
            />

            <EditField
              label="Dimensão"
              value={editForm.dimensao}
              disabled={editLoading}
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, dimensao: value }))
              }
            />

            <EditField
              label="Peso"
              value={editForm.peso}
              disabled={editLoading}
              inputMode="decimal"
              onChange={(value) =>
                setEditForm((prev) => ({ ...prev, peso: value }))
              }
            />
          </div>

          {editError && (
            <div
              role="alert"
              style={{
                marginTop: 16,
                padding: "10px 12px",
                borderRadius: 8,
                background: "#ffecec",
                color: "#842029",
                border: "1px solid #f5c2c7",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
              }}
            >
              {editError}
            </div>
          )}

          {editSuccess && (
            <div
              role="alert"
              style={{
                marginTop: 16,
                padding: "10px 12px",
                borderRadius: 8,
                background: "#e6ffed",
                color: "#0f5132",
                border: "1px solid #badbcc",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
              }}
            >
              {editSuccess}
            </div>
          )}

          <div
            style={{
              marginTop: 22,
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              borderTop: "1px solid #e5e7eb",
              paddingTop: 16,
            }}
          >
            <button
              type="button"
              onClick={handleCloseEditOverlay}
              disabled={editLoading}
              style={{
                height: 38,
                padding: "0 14px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                color: "#0f172a",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                cursor: editLoading ? "not-allowed" : "pointer",
                opacity: editLoading ? 0.6 : 1,
              }}
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleSubmitEditProduto}
              disabled={editLoading}
              aria-busy={editLoading}
              style={{
                height: 38,
                padding: "0 16px",
                borderRadius: 8,
                border: "1px solid #09363f",
                background: "#09363f",
                color: "#fff",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                fontWeight: 700,
                cursor: editLoading ? "not-allowed" : "pointer",
                opacity: editLoading ? 0.75 : 1,
              }}
            >
              {editLoading ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </ModalShell>
      )}

      {showReajusteOverlay && ultimaMovimentacao && (
        <ModalShell
          title="Reajustar último preço de entrada"
          subtitle="Permitido somente para a última movimentação quando ela for uma entrada realizada nos últimos 3 dias."
          ariaLabel="Reajustar último preço de entrada"
          disabled={reajusteLoading}
          onClose={handleCloseReajusteOverlay}
          width="min(760px, 100%)"
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 14,
              marginBottom: 18,
            }}
          >
            <Field
              label="Produto"
              value={`${String(produto?.codigo || codigo)} — ${String(
                produto?.nome || "—"
              )}`}
            />

            <Field
              label="Data da entrada"
              value={formatDateTime(getMovimentoDate(ultimaMovimentacao))}
            />

            <Field
              label="Quantidade da entrada"
              value={formatQuantidade(ultimaMovimentacao.quantidade)}
            />

            <Field
              label="Preço lançado"
              value={formatCurrency(
                ultimaMovimentacao.custo_unitario ?? null,
                normalizeCurrency(ultimaMovimentacao.moeda) || moedaAtual
              )}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 160px",
              gap: 16,
              marginTop: 8,
            }}
          >
            <EditField
              label="Novo custo unitário"
              value={reajusteForm.novo_custo_unitario}
              disabled={reajusteLoading}
              inputMode="decimal"
              onChange={(value) =>
                setReajusteForm((prev) => ({
                  ...prev,
                  novo_custo_unitario: value,
                }))
              }
            />

            <SelectField
              label="Moeda"
              value={reajusteForm.moeda}
              disabled={reajusteLoading}
              onChange={(value) =>
                setReajusteForm((prev) => ({ ...prev, moeda: value }))
              }
            />
          </div>

          <div style={{ marginTop: 16 }}>
            <TextAreaField
              label="Motivo do reajuste"
              value={reajusteForm.motivo}
              disabled={reajusteLoading}
              onChange={(value) =>
                setReajusteForm((prev) => ({ ...prev, motivo: value }))
              }
            />
          </div>

          {reajusteError && (
            <div
              role="alert"
              style={{
                marginTop: 16,
                padding: "10px 12px",
                borderRadius: 8,
                background: "#ffecec",
                color: "#842029",
                border: "1px solid #f5c2c7",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
              }}
            >
              {reajusteError}
            </div>
          )}

          {reajusteSuccess && (
            <div
              role="alert"
              style={{
                marginTop: 16,
                padding: "10px 12px",
                borderRadius: 8,
                background: "#e6ffed",
                color: "#0f5132",
                border: "1px solid #badbcc",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
              }}
            >
              {reajusteSuccess}
            </div>
          )}

          <div
            style={{
              marginTop: 22,
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              borderTop: "1px solid #e5e7eb",
              paddingTop: 16,
            }}
          >
            <button
              type="button"
              onClick={handleCloseReajusteOverlay}
              disabled={reajusteLoading}
              style={{
                height: 38,
                padding: "0 14px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                color: "#0f172a",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                cursor: reajusteLoading ? "not-allowed" : "pointer",
                opacity: reajusteLoading ? 0.6 : 1,
              }}
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={handleSubmitReajustePreco}
              disabled={reajusteLoading}
              aria-busy={reajusteLoading}
              style={{
                height: 38,
                padding: "0 16px",
                borderRadius: 8,
                border: "1px solid #09363f",
                background: "#09363f",
                color: "#fff",
                fontFamily: "Inter, sans-serif",
                fontSize: 13,
                fontWeight: 700,
                cursor: reajusteLoading ? "not-allowed" : "pointer",
                opacity: reajusteLoading ? 0.75 : 1,
              }}
            >
              {reajusteLoading ? "Reajustando..." : "Confirmar reajuste"}
            </button>
          </div>
        </ModalShell>
      )}

      {isOculto && (
        <NoticeBanner type="notify">
          Este produto está marcado como <strong>oculto</strong>. Ele não
          aparecerá nas listagens padrão.
        </NoticeBanner>
      )}

      {error && (
        <NoticeBanner type="error">
          Erro ao carregar dados do produto: {error}
        </NoticeBanner>
      )}

      <div
        style={{
          display: "flex",
          padding: "40px",
          gap: "100px",
          marginLeft: "0px",
        }}
      >
        <div style={{ flex: 1 }}>
          <Field
            label="Nome do Produto"
            value={loading ? "Carregando..." : error ? "—" : nome}
          />
          <Field
            label="Código do Produto"
            value={loading ? "Carregando..." : error ? "—" : codigo}
          />
          <Field
            label="NCM"
            value={loading ? "Carregando..." : error ? "—" : ncm}
          />
          <Field
            label="Classificação"
            value={loading ? "Carregando..." : error ? "—" : classificacao}
          />
          <Field
            label="Estoque"
            value={
              loading
                ? "Carregando..."
                : error
                ? "—"
                : produto?.estoque_nome ||
                  produto?.modulo_label ||
                  moduloAtual?.label ||
                  "—"
            }
          />
        </div>

        <div style={{ flex: 1 }}>
          <Field
            label="Posição"
            value={loading ? "Carregando..." : error ? "—" : posicao}
          />
          <Field
            label="Custo Unitário Atual"
            value={loading ? "Carregando..." : error ? "—" : custoUnitario}
          />
          <Field
            label="Moeda"
            value={loading ? "Carregando..." : error ? "—" : moedaAtual}
          />
          <Field
            label="Quantidade no Estoque"
            value={loading ? "Carregando..." : error ? "—" : quantidadeEstoque}
          />
          <Field
            label="Dimensão"
            value={loading ? "Carregando..." : error ? "—" : dimensao}
          />
          <Field
            label="Peso"
            value={loading ? "Carregando..." : error ? "—" : peso}
          />
        </div>
      </div>

      {ultimaMovimentacao && (
        <div
          style={{
            margin: "0 40px 18px 40px",
            padding: "12px 14px",
            border: "1px solid #d8dee4",
            background: "#f8fafc",
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: "#334155",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <div>
            <strong>Última movimentação:</strong>{" "}
            {String(ultimaMovimentacao.tipo || "Movimentação")} em{" "}
            {formatDateTime(getMovimentoDate(ultimaMovimentacao))}.
            {!podeReajustarUltimoPreco && motivoBloqueioReajuste ? (
              <span style={{ opacity: 0.8 }}>
                {" "}
                Reajuste indisponível: {motivoBloqueioReajuste}
              </span>
            ) : null}
          </div>

          {podeReajustarUltimoPreco && (
            <button
              type="button"
              onClick={handleOpenReajusteOverlay}
              style={smallActionButton}
            >
              Reajustar preço desta entrada
            </button>
          )}
        </div>
      )}

      <div style={toggleWrapStyle} role="group" aria-label="Alternar tabela">
        <button
          type="button"
          onClick={() => setActiveTab("mov")}
          style={activeTab === "mov" ? btnActive : btnInactive}
          aria-pressed={activeTab === "mov"}
        >
          Histórico de Movimentações
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("precos")}
          style={activeTab === "precos" ? btnActive : btnInactive}
          aria-pressed={activeTab === "precos"}
        >
          Histórico de Preços
        </button>
      </div>

      <div style={{ padding: "0 40px", marginLeft: "0px", marginTop: "4px" }}>
        {activeTab === "mov" ? (
          loading ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
              Carregando histórico…
            </div>
          ) : movimentosOrdenados.length === 0 ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
              Sem movimentações para este produto.
            </div>
          ) : (
            <HistoricoWidget
              items={movimentosOrdenados}
              loading={loading}
              error={error}
              formatCurrency={(
                v: string | number | null | undefined,
                m?: string | null
              ) => formatCurrency(v, normalizeCurrency(m) || moedaAtual)}
              formatDateTime={formatDateTime}
            />
          )
        ) : (
          <HistoricoPrecosWidget
            items={precoHistoricoItems}
            loading={loading}
            error={error}
            formatCurrency={(
              v: string | number | null | undefined,
              m?: string | null
            ) => formatCurrency(v, normalizeCurrency(m) || moedaAtual)}
            formatDateTime={formatDateTime}
          />
        )}
      </div>
    </div>
  );
};

export default DetalhesProdutoGenerico;